// src/features/member/services/memberService.ts

import api from '../../../api/axios';

import {
  ADMIN_USER_ENDPOINTS,
  SUPERADMIN_USER_ENDPOINTS,
  USER_ENDPOINTS,
} from '../../../api';

import type {
  ApiResponse,
} from '../../../shared/types/api.types';

import type {
  ActorInfo,
  CreateAdminRequest,
  MemberInfo,
  PresignResponse,
  ResetPasswordRequest,
  UpdateProfileRequest,
} from '../types/member.types';


/**
 * =========================================================
 * 프로필 이미지 설정
 * =========================================================
 */
const ALLOWED_PROFILE_IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'webp',
] as const;

const MAX_PROFILE_IMAGE_SIZE =
  5 * 1024 * 1024;


/**
 * =========================================================
 * 회원 데이터 정규화
 * =========================================================
 */
const normalizeMember = (
  member: MemberInfo,
): MemberInfo => ({
  ...member,

  userId:
    member.userId?.trim() ??
    member.userId,

  userName:
    member.userName?.trim() ??
    member.userName,

  userNickname:
    member.userNickname?.trim() ??
    member.userNickname,

  phoneNumber:
    member.phoneNumber?.trim() ??
    member.phoneNumber,

  code:
    member.code === 'null'
      ? null
      : member.code,
});


/**
 * =========================================================
 * 단건 응답 데이터 검증
 * =========================================================
 */
const requireData = <T>(
  data: T | null | undefined,
  message: string,
): T => {
  if (
    data === null ||
    data === undefined
  ) {
    throw new Error(
      message,
    );
  }

  return data;
};


/**
 * =========================================================
 * 배열 응답 안전 처리
 * =========================================================
 */
const getArrayData = <T>(
  data:
    | T[]
    | null
    | undefined,
): T[] => {
  return Array.isArray(
    data,
  )
    ? data
    : [];
};


/**
 * =========================================================
 * 사용자 로그인 아이디 정규화
 * =========================================================
 */
const normalizeUserId = (
  userId: string,
): string => {
  const normalizedUserId =
    userId.trim();

  if (
    !normalizedUserId
  ) {
    throw new Error(
      '사용자 아이디가 필요합니다.',
    );
  }

  return normalizedUserId;
};


/**
 * =========================================================
 * 프로필 이미지 확장자 정규화
 * =========================================================
 */
const normalizeImageExtension = (
  extension: string,
): string => {
  const normalizedExtension =
    extension
      .replace(
        '.',
        '',
      )
      .trim()
      .toLowerCase();

  if (
    !normalizedExtension
  ) {
    throw new Error(
      '이미지 확장자를 확인할 수 없습니다.',
    );
  }

  if (
    !ALLOWED_PROFILE_IMAGE_EXTENSIONS
      .includes(
        normalizedExtension as
          typeof ALLOWED_PROFILE_IMAGE_EXTENSIONS[number],
      )
  ) {
    throw new Error(
      'JPG, JPEG, PNG, WEBP 이미지만 업로드할 수 있습니다.',
    );
  }

  return normalizedExtension;
};


export const memberService = {
  /**
   * =========================================================
   * 내 정보 조회
   *
   * GET /api/v2/users/me
   *
   * USER / ADMIN / SUPERADMIN
   * =========================================================
   */
  async getMyInfo():
    Promise<MemberInfo> {
    const response =
      await api.get<
        ApiResponse<MemberInfo>
      >(
        USER_ENDPOINTS.ME,
      );

    const data =
      requireData(
        response.data.data,
        '회원 정보를 확인할 수 없습니다.',
      );

    return normalizeMember(
      data,
    );
  },


  /**
   * =========================================================
   * 내 프로필 수정
   *
   * PATCH /api/v2/users/me/profile
   *
   * USER / ADMIN / SUPERADMIN
   * =========================================================
   */
  async updateProfile(
    data: UpdateProfileRequest,
  ): Promise<void> {
    await api.patch(
      USER_ENDPOINTS.PROFILE,
      data,
    );
  },


  /**
   * =========================================================
   * 현재 비밀번호 확인
   *
   * POST /api/v2/users/me/password/check
   *
   * USER / ADMIN / SUPERADMIN
   * =========================================================
   */
  async checkPassword(
    password: string,
  ): Promise<boolean> {
    const response =
      await api.post<
        ApiResponse<boolean>
      >(
        USER_ENDPOINTS
          .PASSWORD_CHECK,
        {
          password,
        },
      );

    return (
      response.data.data ??
      false
    );
  },


  /**
   * =========================================================
   * 내 비밀번호 변경
   *
   * PATCH /api/v2/users/me/password
   *
   * USER / ADMIN / SUPERADMIN
   * =========================================================
   */
  async changePassword(
    currentPassword: string,
    newPassword: string,
  ): Promise<void> {
    await api.patch(
      USER_ENDPOINTS.PASSWORD,
      {
        currentPassword,
        newPassword,
      },
    );
  },


  /**
   * =========================================================
   * 프로필 이미지 Presigned URL 발급
   *
   * POST
   * /api/v2/users/me/profile-image/presign
   *
   * USER / ADMIN / SUPERADMIN
   * =========================================================
   */
  async getProfileImagePresignUrl(
    extension: string,
  ): Promise<PresignResponse> {
    const normalizedExtension =
      normalizeImageExtension(
        extension,
      );

    const response =
      await api.post<
        ApiResponse<PresignResponse>
      >(
        USER_ENDPOINTS
          .PROFILE_IMAGE_PRESIGN,
        {
          extension:
            normalizedExtension,
        },
      );

    return requireData(
      response.data.data,
      '프로필 이미지 업로드 URL을 발급받지 못했습니다.',
    );
  },


  /**
   * =========================================================
   * 프로필 이미지 URL 저장
   *
   * PATCH
   * /api/v2/users/me/profile-image
   *
   * USER / ADMIN / SUPERADMIN
   * =========================================================
   */
  async updateProfileImage(
    imageUrl: string,
  ): Promise<void> {
    const normalizedImageUrl =
      imageUrl.trim();

    if (
      !normalizedImageUrl
    ) {
      throw new Error(
        '프로필 이미지 URL이 올바르지 않습니다.',
      );
    }

    await api.patch(
      USER_ENDPOINTS
        .PROFILE_IMAGE,
      {
        imageUrl:
          normalizedImageUrl,
      },
    );
  },


  /**
   * =========================================================
   * 프로필 이미지 전체 업로드
   *
   * 1. 파일 검증
   * 2. Presigned URL 발급
   * 3. S3 직접 업로드
   * 4. DB에 이미지 URL 저장
   *
   * 반환:
   * imageUrl
   * =========================================================
   */
  async uploadProfileImage(
    file: File,
  ): Promise<string> {
    if (
      !file.type.startsWith(
        'image/',
      )
    ) {
      throw new Error(
        '이미지 파일만 업로드할 수 있습니다.',
      );
    }

    if (
      file.size >
      MAX_PROFILE_IMAGE_SIZE
    ) {
      throw new Error(
        '프로필 이미지는 5MB 이하만 업로드할 수 있습니다.',
      );
    }

    const extension =
      file.name
        .split('.')
        .pop();

    if (
      !extension
    ) {
      throw new Error(
        '이미지 확장자를 확인할 수 없습니다.',
      );
    }

    const normalizedExtension =
      normalizeImageExtension(
        extension,
      );

    const presign =
      await memberService
        .getProfileImagePresignUrl(
          normalizedExtension,
        );

    /**
     * Presigned URL은
     * 백엔드 API가 아니라 S3로 직접 업로드합니다.
     */
    const uploadResponse =
      await fetch(
        presign.uploadUrl,
        {
          method: 'PUT',

          headers: {
            'Content-Type':
              file.type,
          },

          body:
            file,
        },
      );

    if (
      !uploadResponse.ok
    ) {
      throw new Error(
        '프로필 이미지 업로드에 실패했습니다.',
      );
    }

    await memberService
      .updateProfileImage(
        presign.imageUrl,
      );

    return presign.imageUrl;
  },


  /**
   * =========================================================
   * FCM 토큰 저장
   *
   * POST /api/v2/users/me/fcm-token
   *
   * USER / ADMIN / SUPERADMIN
   * =========================================================
   */
  async updateFcmToken(
    token: string,
  ): Promise<void> {
    const normalizedToken =
      token.trim();

    if (
      !normalizedToken
    ) {
      return;
    }

    await api.post(
      USER_ENDPOINTS
        .FCM_TOKEN,
      {
        token:
          normalizedToken,
      },
    );
  },


  /**
   * =========================================================
   * FCM 토큰 삭제
   *
   * DELETE /api/v2/users/me/fcm-token
   *
   * USER / ADMIN / SUPERADMIN
   * =========================================================
   */
  async deleteFcmToken():
    Promise<void> {
    await api.delete(
      USER_ENDPOINTS
        .FCM_TOKEN,
    );
  },


  /**
   * =========================================================
   * 전체 회원 목록 조회
   *
   * GET /api/v2/admin/users
   *
   * ADMIN / SUPERADMIN
   * =========================================================
   */
  async getMembers():
    Promise<MemberInfo[]> {
    const response =
      await api.get<
        ApiResponse<MemberInfo[]>
      >(
        ADMIN_USER_ENDPOINTS.LIST,
      );

    return getArrayData(
      response.data.data,
    ).map(
      normalizeMember,
    );
  },


  /**
   * =========================================================
   * 가입 승인 대기 회원 조회
   *
   * GET /api/v2/admin/users/pending
   *
   * ADMIN / SUPERADMIN
   * =========================================================
   */
  async getPendingMembers():
    Promise<MemberInfo[]> {
    const response =
      await api.get<
        ApiResponse<MemberInfo[]>
      >(
        ADMIN_USER_ENDPOINTS
          .PENDING,
      );

    return getArrayData(
      response.data.data,
    ).map(
      normalizeMember,
    );
  },


  /**
   * =========================================================
   * 회원 검색
   *
   * GET /api/v2/admin/users/search
   *
   * ADMIN / SUPERADMIN
   * =========================================================
   */
  async searchMembers(
    params: {
      userId?: string;
      userName?: string;
      userNickname?: string;
      phoneNumber?: string;
    },
  ): Promise<MemberInfo[]> {
    const response =
      await api.get<
        ApiResponse<MemberInfo[]>
      >(
        ADMIN_USER_ENDPOINTS
          .SEARCH,
        {
          params,
        },
      );

    return getArrayData(
      response.data.data,
    ).map(
      normalizeMember,
    );
  },


  /**
   * =========================================================
   * 사용자 PK 기반 조회
   *
   * GET
   * /api/v2/admin/users/by-id/{id}
   *
   * ADMIN / SUPERADMIN
   * =========================================================
   */
  async getMemberById(
    id: number,
  ): Promise<MemberInfo> {
    const response =
      await api.get<
        ApiResponse<MemberInfo>
      >(
        ADMIN_USER_ENDPOINTS
          .BY_ID(
            id,
          ),
      );

    const data =
      requireData(
        response.data.data,
        '회원 정보를 찾을 수 없습니다.',
      );

    return normalizeMember(
      data,
    );
  },


  /**
   * =========================================================
   * 히스토리 Actor 조회
   *
   * GET
   * /api/v2/admin/users/by-id/{id}
   *
   * ADMIN / SUPERADMIN
   * =========================================================
   */
  async getActorById(
    actorUserId: number,
  ): Promise<ActorInfo> {
    const response =
      await api.get<
        ApiResponse<ActorInfo>
      >(
        ADMIN_USER_ENDPOINTS
          .BY_ID(
            actorUserId,
          ),
      );

    return requireData(
      response.data.data,
      '처리자 정보를 찾을 수 없습니다.',
    );
  },


  /**
   * =========================================================
   * 회원 승인
   *
   * PATCH
   * /api/v2/admin/users/{userId}/approve
   *
   * ADMIN / SUPERADMIN
   * =========================================================
   */
  async approveMember(
    userId: string,
  ): Promise<void> {
    const normalizedUserId =
      normalizeUserId(
        userId,
      );

    await api.patch(
      ADMIN_USER_ENDPOINTS
        .APPROVE(
          normalizedUserId,
        ),
    );
  },


  /**
   * =========================================================
   * 가입 거절
   *
   * DELETE
   * /api/v2/admin/users/{userId}/reject
   *
   * ADMIN / SUPERADMIN
   * =========================================================
   */
  async rejectMember(
    userId: string,
  ): Promise<void> {
    const normalizedUserId =
      normalizeUserId(
        userId,
      );

    await api.delete(
      ADMIN_USER_ENDPOINTS
        .REJECT(
          normalizedUserId,
        ),
    );
  },


  /**
   * =========================================================
   * 회원 활성화
   *
   * PATCH
   * /api/v2/admin/users/{userId}/activate
   *
   * ADMIN / SUPERADMIN
   * =========================================================
   */
  async activateMember(
    userId: string,
  ): Promise<void> {
    const normalizedUserId =
      normalizeUserId(
        userId,
      );

    await api.patch(
      ADMIN_USER_ENDPOINTS
        .ACTIVATE(
          normalizedUserId,
        ),
    );
  },


  /**
   * =========================================================
   * 회원 비활성화
   *
   * PATCH
   * /api/v2/admin/users/{userId}/deactivate
   *
   * ADMIN / SUPERADMIN
   * =========================================================
   */
  async deactivateMember(
    userId: string,
  ): Promise<void> {
    const normalizedUserId =
      normalizeUserId(
        userId,
      );

    await api.patch(
      ADMIN_USER_ENDPOINTS
        .DEACTIVATE(
          normalizedUserId,
        ),
    );
  },


  /**
   * =========================================================
   * ADMIN 방식 회원 비밀번호 초기화
   *
   * PATCH
   * /api/v2/admin/users/password/reset
   *
   * ADMIN / SUPERADMIN
   * =========================================================
   */
  async resetPasswordByAdmin(
    data:
      ResetPasswordRequest,
  ): Promise<void> {
    await api.patch(
      ADMIN_USER_ENDPOINTS
        .RESET_PASSWORD,
      data,
    );
  },


  /**
   * =========================================================
   * SUPERADMIN 회원 비밀번호 강제 초기화
   *
   * PATCH
   * /api/v2/superadmin/users/{userId}/password/reset
   *
   * SUPERADMIN
   * =========================================================
   */
  async resetPasswordBySuperAdmin(
    userId: string,
  ): Promise<void> {
    const normalizedUserId =
      normalizeUserId(
        userId,
      );

    await api.patch(
      SUPERADMIN_USER_ENDPOINTS
        .RESET_PASSWORD(
          normalizedUserId,
        ),
    );
  },


  /**
   * =========================================================
   * ADMIN 계정 목록 조회
   *
   * GET /api/v2/superadmin/admins
   *
   * SUPERADMIN
   * =========================================================
   */
  async getAdminMembers():
    Promise<MemberInfo[]> {
    const response =
      await api.get<
        ApiResponse<MemberInfo[]>
      >(
        SUPERADMIN_USER_ENDPOINTS
          .ACCOUNTS
          .LIST,
      );

    return getArrayData(
      response.data.data,
    ).map(
      normalizeMember,
    );
  },


  /**
   * =========================================================
   * ADMIN 계정 생성
   *
   * POST /api/v2/superadmin/admins
   *
   * SUPERADMIN
   * =========================================================
   */
  async createAdmin(
    data:
      CreateAdminRequest,
  ): Promise<void> {
    await api.post(
      SUPERADMIN_USER_ENDPOINTS
        .ACCOUNTS
        .CREATE,
      data,
    );
  },


  /**
   * =========================================================
   * ADMIN 계정 활성화
   *
   * PATCH
   * /api/v2/superadmin/admins/{adminId}/activate
   *
   * SUPERADMIN
   * =========================================================
   */
  async activateAdmin(
    adminId: number,
  ): Promise<void> {
    if (
      !Number.isInteger(
        adminId,
      ) ||
      adminId <= 0
    ) {
      throw new Error(
        '올바른 관리자 ID가 필요합니다.',
      );
    }

    await api.patch(
      SUPERADMIN_USER_ENDPOINTS
        .ACCOUNTS
        .ACTIVATE(
          adminId,
        ),
    );
  },


  /**
   * =========================================================
   * ADMIN 계정 비활성화
   *
   * PATCH
   * /api/v2/superadmin/admins/{adminId}/deactivate
   *
   * SUPERADMIN
   * =========================================================
   */
  async deactivateAdmin(
    adminId: number,
  ): Promise<void> {
    if (
      !Number.isInteger(
        adminId,
      ) ||
      adminId <= 0
    ) {
      throw new Error(
        '올바른 관리자 ID가 필요합니다.',
      );
    }

    await api.patch(
      SUPERADMIN_USER_ENDPOINTS
        .ACCOUNTS
        .DEACTIVATE(
          adminId,
        ),
    );
  },
};